import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatError } from "../lib/api";
import { useCart } from "../context/CartContext";

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

export default function CartPage() {
  const { items, subtotal, updateQuantity, removeFromCart, clearCart } = useCart();
  const [shipping, setShipping] = useState({
    full_name: "", email: "", phone: "",
    line1: "", line2: "", city: "", state: "LA", postal_code: "", country: "US",
  });
  const [discountCode, setDiscountCode] = useState("");
  const [discount, setDiscount] = useState(null); // {code, discount_amount, ...}
  const [validating, setValidating] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const onShip = (e) => setShipping({ ...shipping, [e.target.name]: e.target.value });

  const applyDiscount = async () => {
    if (!discountCode.trim()) return;
    setValidating(true);
    try {
      const { data } = await api.post("/discounts/validate", {
        code: discountCode.trim(),
        subtotal,
      });
      setDiscount(data);
      toast.success(`Code ${data.code} applied — $${data.discount_amount.toFixed(2)} off`);
    } catch (e) {
      setDiscount(null);
      toast.error(formatError(e));
    } finally {
      setValidating(false);
    }
  };

  const removeDiscount = () => { setDiscount(null); setDiscountCode(""); };

  const checkout = async () => {
    if (items.length === 0) return;
    const required = ["full_name", "email", "line1", "city", "state", "postal_code"];
    for (const f of required) {
      if (!shipping[f]?.trim()) return toast.error(`Please complete: ${f.replace("_", " ")}`);
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/checkout/cart-session", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        shipping,
        discount_code: discount?.code || "",
        origin_url: window.location.origin,
      });
      // Defer clearing — only clear after a successful checkout return.
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatError(e));
      setSubmitting(false);
    }
  };

  const discountAmount = discount?.discount_amount || 0;
  const total = Math.max(0, subtotal - discountAmount);

  if (items.length === 0) {
    return (
      <div className="py-32 px-6 max-w-xl mx-auto text-center" data-testid="cart-empty">
        <div className="overline mb-4">Your Cart</div>
        <h1 className="font-serif text-3xl mb-6">Your cart is empty.</h1>
        <p className="text-white/55 mb-8">Explore the shop and add a candle when one calls you.</p>
        <Link to="/shop" className="inline-block bg-[#8B0000] hover:bg-[#A50000] text-white px-8 py-3 text-[11px] uppercase tracking-[0.2em]">Browse the Shop</Link>
      </div>
    );
  }

  return (
    <div className="py-16 md:py-24 px-6 md:px-10 max-w-6xl mx-auto" data-testid="cart-page">
      <div className="overline mb-4">Checkout</div>
      <h1 className="font-serif text-4xl sm:text-5xl tracking-tight">Your cart.</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 mt-12">
        {/* Left — items + shipping form */}
        <div className="lg:col-span-2 space-y-12">
          <section data-testid="cart-items-section">
            <div className="overline text-[#D4AF37] mb-5">Items</div>
            <ul className="space-y-5">
              {items.map((it) => (
                <li key={it.product_id} className="flex gap-5 border border-white/10 bg-[#0A0A0A] p-4" data-testid={`cart-item-${it.product_id}`}>
                  <img src={it.image_url} alt={it.name} className="w-24 h-24 object-cover" />
                  <div className="flex-1 min-w-0">
                    <div className="font-serif text-lg">{it.name}</div>
                    <div className="text-white/55 text-sm mt-1">${it.price.toFixed(2)} each</div>
                    <div className="mt-3 flex items-center gap-2">
                      <button onClick={() => updateQuantity(it.product_id, it.quantity - 1)} className="w-8 h-8 border border-white/20 hover:border-[#D4AF37]">−</button>
                      <span className="w-10 text-center">{it.quantity}</span>
                      <button onClick={() => updateQuantity(it.product_id, it.quantity + 1)} className="w-8 h-8 border border-white/20 hover:border-[#D4AF37]">+</button>
                      <button onClick={() => removeFromCart(it.product_id)} className="ml-auto text-[10px] uppercase tracking-[0.18em] text-white/45 hover:text-[#8B0000]">Remove</button>
                    </div>
                  </div>
                  <div className="font-serif text-lg">${(it.price * it.quantity).toFixed(2)}</div>
                </li>
              ))}
            </ul>
            <button onClick={clearCart} className="mt-4 text-[10px] uppercase tracking-[0.2em] text-white/40 hover:text-[#8B0000]" data-testid="cart-clear">Clear Cart</button>
          </section>

          <section data-testid="shipping-section">
            <div className="overline text-[#D4AF37] mb-5">Shipping Address</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-6">
              <SF label="Full Name" name="full_name" value={shipping.full_name} onChange={onShip} required testid="ship-name" />
              <SF label="Email" name="email" type="email" value={shipping.email} onChange={onShip} required testid="ship-email" />
              <SF label="Phone" name="phone" value={shipping.phone} onChange={onShip} testid="ship-phone" />
              <div />
              <div className="sm:col-span-2"><SF label="Street Address" name="line1" value={shipping.line1} onChange={onShip} required testid="ship-line1" /></div>
              <div className="sm:col-span-2"><SF label="Apartment / Suite (optional)" name="line2" value={shipping.line2} onChange={onShip} testid="ship-line2" /></div>
              <SF label="City" name="city" value={shipping.city} onChange={onShip} required testid="ship-city" />
              <div>
                <label className="overline block mb-3">State *</label>
                <select name="state" value={shipping.state} onChange={onShip} className="w-full bg-transparent border-b border-white/20 py-3 text-white focus:border-[#D4AF37] outline-none" data-testid="ship-state">
                  {US_STATES.map((s) => <option key={s} value={s} className="bg-black">{s}</option>)}
                </select>
              </div>
              <SF label="Postal Code" name="postal_code" value={shipping.postal_code} onChange={onShip} required testid="ship-postal" />
              <SF label="Country" name="country" value={shipping.country} onChange={onShip} testid="ship-country" />
            </div>
          </section>
        </div>

        {/* Right — summary */}
        <aside className="lg:col-span-1 lg:sticky lg:top-24 h-fit" data-testid="cart-summary">
          <div className="bg-[#0A0A0A] border border-[#D4AF37]/25 p-6">
            <div className="overline text-[#D4AF37] mb-4">Order Summary</div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span>Subtotal</span><span data-testid="summary-subtotal">${subtotal.toFixed(2)}</span></div>
              {discount && (
                <div className="flex justify-between text-[#D4AF37]" data-testid="summary-discount-row">
                  <span>Discount ({discount.code})</span>
                  <span>-${discountAmount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-white/45"><span>Shipping</span><span>Calculated at checkout</span></div>
              <div className="flex justify-between border-t border-white/10 pt-3 mt-3">
                <span className="font-serif text-lg">Total</span>
                <span className="font-serif text-2xl" data-testid="summary-total">${total.toFixed(2)}</span>
              </div>
            </div>

            <div className="mt-6">
              <label className="overline block mb-2">Discount Code</label>
              {discount ? (
                <div className="flex items-center justify-between border border-[#D4AF37]/40 px-3 py-2 text-sm" data-testid="summary-discount-applied">
                  <span><strong className="text-[#D4AF37]">{discount.code}</strong> applied</span>
                  <button onClick={removeDiscount} className="text-[10px] uppercase tracking-[0.18em] text-white/50 hover:text-[#8B0000]">Remove</button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    value={discountCode}
                    onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                    placeholder="ENTER CODE"
                    className="flex-1 bg-transparent border-b border-white/20 py-2 text-white text-sm focus:border-[#D4AF37] outline-none uppercase tracking-wider"
                    data-testid="discount-input"
                  />
                  <button onClick={applyDiscount} disabled={validating || !discountCode.trim()} className="text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] hover:text-white disabled:opacity-50" data-testid="discount-apply-btn">
                    {validating ? "..." : "Apply"}
                  </button>
                </div>
              )}
            </div>

            <button
              onClick={checkout}
              disabled={submitting}
              className="w-full mt-6 bg-[#8B0000] hover:bg-[#A50000] disabled:opacity-50 text-white px-6 py-4 text-[11px] uppercase tracking-[0.2em]"
              data-testid="cart-checkout-btn"
            >
              {submitting ? "Processing..." : "Continue to Payment"}
            </button>
            <p className="mt-3 text-[10px] text-white/40 text-center">Secure checkout powered by Stripe.</p>
          </div>
        </aside>
      </div>
    </div>
  );
}

function SF({ label, name, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="overline block mb-3">{label}{required && " *"}</label>
      <input
        type={type} name={name} value={value} onChange={onChange} required={required}
        className="w-full bg-transparent border-b border-white/20 py-3 text-white focus:border-[#D4AF37] outline-none"
        data-testid={testid}
      />
    </div>
  );
}
